import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { formatDate, formatNumber } from '@/services/format';
import type { MetricDetail } from '@/types';

export function MetricDetailPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<MetricDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    (async () => {
      try {
        setData(await adminApi.metricDetail(token));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível carregar a métrica');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-gray-500">A carregar…</div>;
  if (error) return <div className="p-8 text-magni-red-600">{error}</div>;
  if (!data) return <div className="p-8 text-gray-500">Métrica não encontrada.</div>;

  const periodMax = Math.max(1, ...data.byPeriod.map(p => p.totalQuantity));
  const clientMax = Math.max(1, ...data.byClient.map(c => c.totalQuantity));
  const featureMax = Math.max(1, ...data.byFeature.map(f => f.totalQuantity));

  return (
    <div className="p-8 max-w-[1280px] mx-auto">
      <Link to="/metrics" className="inline-flex items-center gap-1 text-sm text-magni-red hover:underline mb-3">
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        Métricas
      </Link>
      <h1 className="page-h1 font-mono mb-8">{data.token}</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <Tile label="Quantidade total" value={data.totalQuantity} />
        <Tile label="Registos" value={data.recordCount} />
        <Tile label="Clientes distintos" value={data.clientCount} />
      </div>

      <Section title={`Por período (${data.byPeriod.length} mais recentes)`}>
        {data.byPeriod.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Início do período</th>
                <th className="px-6 py-3 font-semibold">Fim (excl.)</th>
                <th className="px-6 py-3 font-semibold text-right">Registos</th>
                <th className="px-6 py-3 font-semibold text-right w-1/3">Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {data.byPeriod.map((p, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-6 py-3 text-gray-800">{formatDate(p.periodStart)}</td>
                  <td className="px-6 py-3 text-gray-800">{formatDate(p.periodEndExclusive)}</td>
                  <td className="px-6 py-3 text-right text-gray-800">{formatNumber(p.recordCount)}</td>
                  <td className="px-6 py-3 text-right"><Bar value={p.totalQuantity} max={periodMax} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={`Principais clientes (${data.byClient.length})`}>
        {data.byClient.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">ID do cliente</th>
                <th className="px-6 py-3 font-semibold text-right w-2/3">Quantidade total</th>
              </tr>
            </thead>
            <tbody>
              {data.byClient.map(c => (
                <tr key={c.clientId} className="border-t border-gray-100">
                  <td className="px-6 py-3 font-mono text-gray-800">{c.clientId}</td>
                  <td className="px-6 py-3 text-right"><Bar value={c.totalQuantity} max={clientMax} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={`Por funcionalidade (${data.byFeature.length})`}>
        {data.byFeature.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Token da funcionalidade</th>
                <th className="px-6 py-3 font-semibold text-right w-2/3">Quantidade total</th>
              </tr>
            </thead>
            <tbody>
              {data.byFeature.map(f => (
                <tr key={f.featureToken} className="border-t border-gray-100">
                  <td className="px-6 py-3 font-mono text-xs text-gray-800">{f.featureToken}</td>
                  <td className="px-6 py-3 text-right"><Bar value={f.totalQuantity} max={featureMax} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="card">
      <p className="eyebrow mb-2">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{formatNumber(value)}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      {/* Section heading uses the eyebrow rule (small, uppercase, magni-red) per design system. */}
      <h2 className="eyebrow mb-3">{title}</h2>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">{children}</div>
    </section>
  );
}

// Data-viz bars are intentionally neutral: the design system reserves
// magni-red for "the one thing the user should look at", which in a long
// table is not every row. Use gray-200 for the track and gray-500 for
// the proportional fill.
function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center justify-end gap-3">
      <span className="text-gray-800 tabular-nums">{formatNumber(value)}</span>
      <div className="w-32 h-2 bg-gray-100 rounded-pill overflow-hidden">
        <div className="h-2 bg-gray-500 rounded-pill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Empty() {
  return <p className="px-6 py-6 text-center text-gray-500">Sem dados.</p>;
}
